import { DriveItemListOrigin } from '../containers/DriveItemListPage/constants';
import { Api } from '../typings/api';
import RoutesUtils from './routes';
import UserAgentUtils from './userAgent';
import Utils from './';
import { AuthenticationUserState, Security, ConnectionStatus } from '../containers/Authentication/reducer';
import { GalleryInfosState } from '../containers/Gallery/reducer';
import { Server, ASP_SERVERS } from '../containers/MultiAppAuthentication/constants';
import DriveItemUtils from './driveItem';
import ApiUtils from './api';
import LocalStorageManager from './localStorage/manager';
import store from '../store';

enum ApiUrls {
	AUTH = 'services/rest/loginWs/',
	MEMBERSHIP = 'services/rest/membershipWs/',
	DRIVE = 'services/rest/workspaceManagerWs/',
	ENRICHMENT = 'services/rest/enrichmentWs/',
	GENERATION = 'services/rest/generationWs/',
	COMPONENT = 'services/rest/pageManagerWs/',
}

class UrlUtils {
	// ----- URL Generators
	// returns a the result of a final URL function
	public static getUrlAuthentication(galleryConfig: Api.Config, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathAuthentication(keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}


	public static getUrlHome(galleryConfig: Api.Config, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathHome(keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlParameters(galleryConfig: Api.Config, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathParameters(keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlDriveItemListPage(galleryConfig: Api.Config, origin: DriveItemListOrigin, originParam: string | null, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathDriveItemListPage(origin, originParam, keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlDriveItemDetailPage(galleryConfig: Api.Config, driveItemId: number, driveFolderId: number|undefined, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathDriveItemDetailPage(driveItemId, driveFolderId, keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlTheater(galleryConfig: Api.Config, origin: DriveItemListOrigin, originParam: string | null, driveItemId: number, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathTheater(origin, originParam, driveItemId, keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getApiGalleryUrl(galleryConfig: Api.Config, api: string) {
		return this.getApiItemUrl(galleryConfig, galleryConfig.applicationGallery, api);
	}

	public static getChildrenUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_children.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getChildrenMetaUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_childrenMeta.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getAppMetaUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_appMeta.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getMetaUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_meta.json`;
		const url = UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);

		return url;
	}

	public static getContentBlockUrl(galleryConfig: Api.Config, driveItemId: number, block: number) {
		const api = `_content-block-${block}.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getResourceZipUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_resource.zip`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api, true);
	}

	public static getOutcomingsUrl = (galleryConfig: Api.Config, driveItemId: number): string => {
		const api = `_outcomings.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getAttachmentUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_attachment`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getNewContentsUrl(galleryConfig: Api.Config, sinceDate: Date) {
		const api = `_new.json?sinceDate=${sinceDate.getTime()}`;

		return UrlUtils.getApiGalleryUrl(galleryConfig, api);
	}

	public static getSearchUrl(galleryConfig: Api.Config, searchString: string) {
		let api = '_search.json?expr=';
		if (searchString) {
			const regex = /tags\[(.*)\]/g;
			const match = regex.exec(searchString);
			if (match) {
				// Remove the match from the searchString
				api += encodeURIComponent(searchString.replace(match[0], '').trim());
				// Add each tag to the url
				const tagsArray = match[1].split(',');
				tagsArray.forEach(tag => {
					api += `&tag=${encodeURIComponent(tag.trim())}`;
				});
			} else {
				api += encodeURIComponent(searchString);
			}
		}
		return UrlUtils.getApiGalleryUrl(galleryConfig, api);
	}

	public static getWelcomePageUrl = (galleryInfos: GalleryInfosState): string => {
		const { template, config } = galleryInfos;
		if (template && template.welcomepage && template.welcomepage.url) {
			return UrlUtils.getItemUrl(config, Utils.getMediaId(template.welcomepage.url));
		}
		return '';
	}

	public static getTemplateUrl(galleryConfig: Api.Config, server?: Server) {
		const fileName = `template.json`;
		if (server) {
			const driveUrl = ASP_SERVERS[server].driveUrl;
			const templateUrl = `${driveUrl}${galleryConfig.clientId}/${galleryConfig.applicationGallery}/.gallery/config/${fileName}`;

			return UrlUtils.addCredentialsIfNeeded(templateUrl);
		}

		return UrlUtils.getConfigFileUrl(galleryConfig, fileName);
	}

	public static getConfigUrl(server: Server, clientId: number, galleryId: number): string {
		const driveUrl = ASP_SERVERS[server].driveUrl;
		const url = `${driveUrl}${clientId}/${galleryId}/.gallery/config/config.json`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	public static getStatisticsUrl(galleryConfig: Api.Config) {
		const fileName = `statistics.xml`;

		return UrlUtils.getConfigFileUrl(galleryConfig, fileName);
	}

	public static getLoginJsonUrl(galleryConfig: Api.Config) {
		return `${this.getRootApiGallery(galleryConfig)}_login.json`;
	}

	public static getSamlAuthenticationPage(params: string) {
		return `https://fr.zone-secure.net/saml-login/${params}`;
	}

	public static getMessageInterceptorUrl(server: Server) {
		const driveUrl = ASP_SERVERS[server].driveUrl;
		return `${driveUrl}messageInterceptor.html`;
	}

	public static isSamlIframeRedirectionUrl(iframeLocation: string, appConfig: Api.Config) {
		const galleryUrl = `${this.getRootApiGallery(appConfig)}`;
		return iframeLocation.includes(galleryUrl);
	}

	// ----- Final URLs
	// complete URL with credential if needed

	// Don't add credential to this url, url shared to contacts
	public static getGallAppUrl(galleryConfig: Api.Config, hash: string) {
		return `${this.getRootApiGallery(galleryConfig)}/#${hash}`;
	}

	public static getLoginToAspUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}appLogin`);
	}

	public static getLogoutFromAspUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}logout`);
	}

	public static getLogoutFromApplication(galleryConfig: Api.Config, isDifferentAppUrls?: boolean): string {
		// there can be different application urls from ASP_SERVERS and from galleryConfig
		if (isDifferentAppUrls) {
			const { galleryInfos: { config: { clientId, applicationGallery } } } = store.getState();
			const appUrl = ASP_SERVERS[ApiUtils.multiAppAuthenticationServer].driveUrl;
			return `${appUrl}${clientId}/${applicationGallery}_logout`;
		}

		return `${UrlUtils.getApplicationUrl(galleryConfig)}_logout`;
	}

	public static getResetAspPasswordUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}react-admin/#/forgot_password`);
	}

	public static getCreateDemoAccountUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}createDemoAccount`);
	}

	public static getCrmAccountsUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}getCrmContacts`);
	}

	public static getAuthMethodsUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}getAuthMethods`);
	}

	public static getOrgLoginUrl(galleryConfig: Api.Config, orgName?: string): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}orgLogin${orgName ? `?org=${encodeURIComponent(orgName)}` : ''}`);
	}

	public static getLoginUrl(galleryConfig: Api.Config, login: string, password: string, method: Security) {
		const url = UrlUtils.getLoginJsonUrl(galleryConfig);
		const userInfos = {
			needCredentials: true,
			login,
			password,
			mode: {
				method,
				status: ConnectionStatus.CONNECTED
			}
		};
		return UrlUtils.addCredentialsIfNeeded(url, false, userInfos);
	}

	public static getCheckSessionUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}check`);
	}

	public static getResolutionsUrl(galleryConfig: Api.Config) {
		const url = `${this.getRootApiGallery(galleryConfig)}/.drive/resolutions.json`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	public static getItemOriginalFileUrl(galleryConfig: Api.Config, originalFile: string) {
		const url = `${UrlUtils.getRootApiClient(galleryConfig)}/${originalFile}`;

		return Utils.getUrlForSecuredResource(UrlUtils.addCredentialsIfNeeded(url));
	}

	public static getImageUrl(galleryConfig: Api.Config, driveItem: Api.DriveItem, size: Api.ResolutionSize, extension: string) {
		const url = `${this.getRootApiItem(galleryConfig, DriveItemUtils.getIconId(driveItem))}_${size.box ? 'box-' : ''}${size.width}x${size.height}.${extension}`;

		return Utils.getUrlForSecuredResource(UrlUtils.addCredentialsIfNeeded(url));
	}

	public static getPublicationUrl(
		galleryConfig: Api.Config, driveItem: Api.DriveItem, page: number = 1, isForMobileApp: boolean = false,
		isShare: boolean = false, hideWelcomePage: boolean = false
	): string {
		let publicationUrlParams = this.getPublicationUrlParams(page, isForMobileApp, hideWelcomePage);

		const url = `${this.getRootApiItem(galleryConfig, driveItem.id)}/index.html${publicationUrlParams}`;

		if (isShare) {
			return url;
		}

		return Utils.getUrlForSecuredResource(UrlUtils.addCredentialsIfNeeded(url));
	}

	public static getPublicationUrlParams(page: number = 1, isForMobileApp: boolean = false, hideWelcomePage: boolean = false) {
		let publicationUrlParams = '?';

		if (page !== 1) {
			const startPageParam = `startPage=${page}&`;
			publicationUrlParams += startPageParam;
		}
		if (isForMobileApp) {
			// nativeReq param is needed to handle a case when calls are made to several domains (different application urls from ASP_SERVERS and from galleryConfig)
			const mobileAppParameters = 'isApp=true&backHome=true&nativeReq=true&';
			publicationUrlParams += mobileAppParameters;
		}
		if (hideWelcomePage) {
			const hideWelcomePageParam = 'initState=nowelcomepage';
			publicationUrlParams += hideWelcomePageParam;
		}

		// Remove "?" from URL if no params are added
		if (page === 1 && !isForMobileApp && !hideWelcomePage) {
			publicationUrlParams = '';
		}

		return publicationUrlParams;
	}

	public static getPublicationCoverUrl(galleryConfig: Api.Config, driveItem: Api.DriveItem, pageIndex: number) {
		const rootUrl = UrlUtils.getRootApiClient(galleryConfig);
		const cover = driveItem.thumbsPath ? DriveItemUtils.getPublicationCover(driveItem.thumbsPath, pageIndex + 1) : '';
		const url = `${rootUrl}/${driveItem.id}/${cover}`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}


	// Credentials not needed for this API, bug ?
	public static getResourcesGroupUrl = (galleryConfig: Api.Config, ids: number[]): string => {
		const { service, clientId } = galleryConfig;
		const parsedIds = JSON.stringify(ids);
		return service ? Utils.getUrlWithCorrectProtocol(`${service.host}/${service.download.service}?clientId=${clientId}&ids=${parsedIds}&token=${service.token}`) : '';
	}

	public static getShareServiceUrl = (galleryConfig: Api.Config): string => {
		const { applicationGallery } = galleryConfig;
		const shareServiceUrl = `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}/${applicationGallery}_share`;
		return Utils.getUrlWithHttpsProtocol(UrlUtils.addCredentialsIfNeeded(shareServiceUrl));
	}

	public static getCreatePrivateFolderUrl = (galleryConfig: Api.Config): string => {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}createDriveWithLinks`);
	}

	public static getCreateExternalLinkUrl = (galleryConfig: Api.Config): string => {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}createExternalLink`);
	}

	public static getComponentUrl = (galleryInfos: GalleryInfosState, componentId: number): string => {
		const { config } = galleryInfos;
		const url = UrlUtils.getRootApiItem(config, componentId) + '/index.html';
		return Utils.getUrlForSecuredResource(UrlUtils.addCredentialsIfNeeded(url));
	}

	public static getRootApiClient(galleryConfig: Api.Config) {
		const { clientId } = galleryConfig;

		const isMaster = Utils.isMaster(galleryConfig);
		if (isMaster) {
			return this.getMasterRootApi(galleryConfig);
		}

		const isMobileApp = UserAgentUtils.isMobileApp;
		if (isMobileApp || process.env.NODE_ENV !== 'production') {
			return `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}`;
		}

		return `${this.getRelativeRootApi(galleryConfig)}/${clientId}`;
	}

	public static getConfiguredRootApiClient(galleryConfig: Api.Config) {
		const { clientId } = galleryConfig;
		return `${this.getConfiguredRootApi(galleryConfig)}/${clientId}`;
	}

	public static getRootApiGallery(galleryConfig: Api.Config) {

		const isMobileApp = UserAgentUtils.isMobileApp;
		if (isMobileApp) {
			const { applicationGallery } = galleryConfig;
			return this.getRootApiItem(galleryConfig, applicationGallery);
		}

		return `${window.location.origin}${window.location.pathname}`;
	}

	public static getIndesignEditorDataUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.ENRICHMENT}getPubliEditData`);
	}

	public static getHtmlEditorDataUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.COMPONENT}getPageEnrichmentInfo`);
	}

	public static getEditorResourceUrl(galleryConfig: Api.Config, resourceId?: number, relPath?: string) {
		const resourceRoot = UrlUtils.getRootApiItem(galleryConfig, resourceId);
		return Utils.getUrlWithHttpsProtocol(`${resourceRoot}${relPath ? '/' + relPath : ''}`);
	}

	public static getSaveIndesignEditorPageUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.ENRICHMENT}saveEditedEpubPage`);
	}

	public static getSaveIndesignEditorConfigsUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.ENRICHMENT}savePubliConfigsJson`);
	}

	public static getSaveHtmlEditorPageUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.COMPONENT}saveComponentPage`);
	}

	public static getCreateComboUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}createComboPublication`);
	}

	public static getPublicationProgressUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}getPublicationProgress`);
	}

	public static getImportFileUrl(galleryConfig: Api.Config, isOptimizedImageImport?: boolean) {
		const host = UrlUtils.getHost(galleryConfig);
		const service = isOptimizedImageImport ? 'importImage' : 'importFile';
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}${service}`);
	}

	public static getSetAsTemplateUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}createProposalTpl`);
	}

	public static getRemoveTemplateUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}deleteProposalTpl`);
	}

	public static getProposalTemplatesUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_templates.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getCreateProposalFromMasterProposalUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}copyPublication`);
	}

	public static getCreateProposalFromDesktopFileUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}createProposal`);
	}

	public static getCreateProposalFromDriveItemUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}convertToProposal`);
	}

	public static getShareProposalUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}createSharedResourceLink`);
	}

	public static getRenameResourceUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}updateResourceName`);
	}

	public static getTrashResourcesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}trashResources`);
	}

	public static getRemovePublicationPagesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}deletePublicationPages`);
	}

	public static getMovePublicationPagesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}movePages`);
	}

	public static getDuplicatePublicationPageUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}duplicatePage`);
	}

	public static getApplicationUrl(galleryConfig: Api.Config) {
		const { applicationGallery } = galleryConfig;
		return Utils.getUrlWithHttpsProtocol(this.getRootApiItem(galleryConfig, applicationGallery));
	}

	public static getProfileDataUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}getProfileInfo`);
	}

	public static getEditProfileDataUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}editProfile`);
	}

	public static getProposalsByTypeUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}getProposalsByType`);
	}

	public static getMoveResourcesOrderUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}moveResourcesOrder`);
	}

	public static getMoveResourcesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}moveResources`);
	}

	public static addCredentialsToCover(url: string) {
		return UrlUtils.addCredentialsIfNeeded(url);
	}

	private static getItemUrl(galleryConfig: Api.Config, driveItemId: number) {
		const url = UrlUtils.getRootApiItem(galleryConfig, driveItemId);

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	private static getApiItemUrl(galleryConfig: Api.Config, driveItemId: number, api: string, needToken: boolean = false) {
		const url = `${UrlUtils.getRootApiItem(galleryConfig, driveItemId)}${api}`;

		return UrlUtils.addCredentialsIfNeeded(url, needToken);
	}

	private static getConfigFileUrl(galleryConfig: Api.Config, fileName: string) {
		const url = `${this.getRootApiGallery(galleryConfig)}/.gallery/config/${fileName}`;

		return UrlUtils.addCredentialsIfNeeded(url);
	}

	// ----- Root URLs
	// not used directly, Final Urls append things to it and add credentials if needed

	private static getRootApiItem(galleryConfig: Api.Config, driveItemId?: number) {
		return `${this.getRootApiClient(galleryConfig)}${driveItemId ? '/' + driveItemId : ''}`;
	}

	private static getMasterRootApi(galleryConfig: Api.Config): string {
		const { mediaPath } = galleryConfig;
		return `${mediaPath ? mediaPath : ''}`;
	}

	private static getConfiguredRootApi(galleryConfig: Api.Config): string {
		const { applicationUrl } = galleryConfig;
		return Utils.getUrlWithCorrectProtocol(applicationUrl);
	}

	private static getRelativeRootApi(galleryConfig: Api.Config): string {
		const { applicationUrl } = galleryConfig;

		// get the part before the /-/${customClientPath}/-/ or /${clientId}/${galleryId}
		const relativeRootApiRegex = /^(.*?)(\/[0-9]+\/[0-9]+\/?|\/-\/.*\/-)$/;
		const regexMatch = relativeRootApiRegex.exec(window.location.origin + window.location.pathname);
		const relativeRootApi = regexMatch ? regexMatch[1] : applicationUrl;

		return Utils.getUrlWithCorrectProtocol(relativeRootApi);
	}

	// ----- Helpers
	private static addCredentialsIfNeeded(url: string, needToken: boolean = false, userInfos?: AuthenticationUserState): string {
		const galleryCredentials = Utils.getGalleryCredentials(userInfos);
		const multiAppCredentials = Utils.getMultiAppCredentials();
		const token = ApiUtils.token;

		if (!multiAppCredentials && !galleryCredentials) {
			return url;
		}

		let login;
		let password;
		if (multiAppCredentials) {
			login = multiAppCredentials.login;
			password = multiAppCredentials.password;
		} else if (galleryCredentials) {
			login = galleryCredentials.login;
			password = galleryCredentials.password;
		}

		const includesSeparator = url.includes('?');
		const startingSeparator = includesSeparator ? '&' : '?';
		const params: string[] = [];
		if (login && password) {
			params.push(`user=${encodeURIComponent(login)}`);
			params.push(`pass=${encodeURIComponent(password)}`);
		}
		if (needToken && token) {
			params.push(`token=${encodeURIComponent(token)}`);
		}

		return `${url}${startingSeparator}${params.join('&')}`;
	}

	private static getHost(galleryConfig: Api.Config): string {
		const isPreview = LocalStorageManager.getUsername() === null;
		let host = '';

		if (UserAgentUtils.isMobileApp) {
			host = ASP_SERVERS[ApiUtils.multiAppAuthenticationServer].url;
		} else if (galleryConfig.service) {
			const { host: configHost, directHost: configDirectHost } = galleryConfig.service;
			host = isPreview ? configHost : configDirectHost || configHost;
		}
		return host[host.length - 1] === '/' ? host : host + '/';
	}
}

export const urlUtils = new UrlUtils;

export default UrlUtils;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/url.ts